import getConfigValue from '@/config';
import { useLazyCheckAccountsExportLimitQuery, useLazyCreateAccountsExportRequestQuery } from '@/services/accounts';
import { downloadFile } from '@/services/helpers';
import { Button, toast, useBreakpoint, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ExportAcknowledgementModal } from './AccountsExportAcknowledgementModal';

const api = getConfigValue('VITE_APP_API_BASE_URL');

/**
 * Translates frontend URL parameters to the actual API query parameters.
 * The frontend uses shorthand URL params like `?postActivation` and `?cancelled`
 * which need to be converted to the full API parameters that the export endpoint expects.
 *
 * This mirrors the logic in AccountsSummary.tsx to ensure consistency between
 * the accounts list view and the export functionality.
 */
export function buildExportQueryString(searchParams: URLSearchParams): string {
  const exportParams = new URLSearchParams();

  // Check for frontend-only URL signals and translate to API params
  const showPostActivation = searchParams.has('postActivation');
  const showCancelled = searchParams.has('cancelled');

  // Check for aging filter in advancedFilters (same logic as AccountsSummary)
  const advancedFilters = searchParams.get('advancedFilters');
  let agingFilterEnabled = false;
  if (advancedFilters) {
    try {
      const parsedFilters = JSON.parse(advancedFilters);
      agingFilterEnabled = parsedFilters.some((filter: { id: string }) => filter.id === 'agingAccounts');
    } catch {
      // Invalid JSON - continue without the aging filter check
      console.warn('Invalid advancedFilters JSON in export query:', advancedFilters);
    }
  }

  // Copy over standard params, translating 'search' to 'searchTerm'
  searchParams.forEach((value, key) => {
    if (key === 'search') {
      exportParams.set('searchTerm', value);
    } else if (key !== 'postActivation' && key !== 'cancelled' && key !== 'page') {
      // Skip frontend-only params and pagination (export doesn't paginate)
      exportParams.set(key, value);
    }
  });

  // Apply the same filter logic that AccountsSummary uses
  if (showPostActivation) {
    exportParams.set('onlyPostActivationAccounts', 'true');
  }

  if (showCancelled) {
    exportParams.set('onlyCancelledAccounts', 'true');
  }

  // includeCompletedAccounts must be true when showing post-activation, cancelled, or aging accounts.
  // Note: Per the API schema, onlyCancelledAccounts requires includeCompletedAccounts=true.
  // This is more correct than AccountsSummary.tsx which doesn't set it for cancelled accounts.
  if (showPostActivation || showCancelled || agingFilterEnabled) {
    exportParams.set('includeCompletedAccounts', 'true');
  }

  const queryString = exportParams.toString();
  return queryString ? `?${queryString}` : '';
}

export function AccountsExportAction() {
  const { isPhone } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportCheck, { isFetching: isCheckFetching }] = useLazyCheckAccountsExportLimitQuery();
  const [createExportRequest, { isFetching: isExportRequestFetching }] = useLazyCreateAccountsExportRequestQuery();
  const isLoading = isCheckFetching || isExportRequestFetching || isDownloading;
  const { isOpen, handleOpen: openModal, handleClose } = useOpenClose();

  const handleExportClick = useCallback(async () => {
    try {
      const exportQueryString = buildExportQueryString(searchParams);
      const checkResult = await exportCheck(exportQueryString, false).unwrap();

      if (checkResult.totalWithinLimit) {
        setIsDownloading(true);
        await downloadFile(`${api}/accounts/export/csv${exportQueryString}`, `accounts_export_v1_${Date.now()}.csv`);
      } else {
        await createExportRequest(exportQueryString).unwrap();
        openModal();
      }
    } catch (error) {
      console.error('Error requesting an export.', error);
      toast.error('Error requesting an export. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  }, [createExportRequest, exportCheck, searchParams, openModal]);

  return (
    <>
      <Button
        variant="secondary"
        tone="neutral"
        iconPrefix="download"
        size={{ base: 'sm', desktop: 'md' }}
        aria-label="Export"
        onClick={handleExportClick}
        isLoading={isLoading}
        isDisabled={isLoading}
      >
        {!isPhone && 'Export'}
      </Button>
      <ExportAcknowledgementModal handleClose={handleClose} isOpen={isOpen} />
    </>
  );
}
